import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import {
  BookMarked,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ChevronRight,
  Loader2,
  AlertCircle,
  Code,
  ChevronDown,
  ChevronUp,
  Shield,
  Zap,
  FileText,
  Clapperboard,
  Video,
  Package,
} from 'lucide-react';
import { useClientCoverageStore } from '@/stores/skillsIntelligence';
import { useSkillRegistryStore, DEFAULT_ORG } from '@/stores/skillRegistry';
import type { ClientCoverage } from '@/services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TOOL_META: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  get_creative_brief: { label: 'Creative Brief', icon: FileText, color: 'text-blue-500' },
  generate_creative_angles: { label: 'Creative Angles', icon: Zap, color: 'text-amber-500' },
  generate_ugc_script: { label: 'UGC Script', icon: Clapperboard, color: 'text-purple-500' },
  generate_video_prompts: { label: 'Video Prompts', icon: Video, color: 'text-rose-500' },
  get_ad_pack: { label: 'Ad Pack', icon: Package, color: 'text-emerald-500' },
};

function CoverageBar({ pct }: { pct: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={clsx(
            'h-full rounded-full transition-all',
            pct === 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span
        className={clsx(
          'text-[11px] font-semibold tabular-nums w-8 text-right',
          pct === 100 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-red-500',
        )}
      >
        {pct}%
      </span>
    </div>
  );
}

function StatusIcon({ status }: { status: ClientCoverage['status'] }) {
  if (status === 'full') return <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />;
  if (status === 'partial') return <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />;
  return <XCircle size={16} className="text-red-400 flex-shrink-0" />;
}

// ---------------------------------------------------------------------------
// Mock binding detail per client / tool
// ---------------------------------------------------------------------------

const MOCK_BINDINGS: Record<string, Record<string, { version: string; published_by: string; snippet: string }>> = {
  skoutorganic: {
    get_creative_brief: {
      version: '2.1.0',
      published_by: 'garrett@neurograph.io',
      snippet: JSON.stringify(
        {
          required_sections: ['persona_profile', 'lunchbox_strategist_context', 'product_highlight', 'channel_directives'],
          tone: 'warm, aspirational, parent-first',
          exclude_sections: ['competitor_analysis'],
        },
        null,
        2,
      ),
    },
    generate_creative_angles: {
      version: '1.1.0',
      published_by: 'garrett@neurograph.io',
      snippet: JSON.stringify(
        {
          preferred_archetypes: ['aspiration', 'social_proof', 'transformation'],
          num_angles: 6,
          required_hooks: ['child_interaction', 'lunchbox_scene'],
        },
        null,
        2,
      ),
    },
    generate_ugc_script: {
      version: '1.0.0',
      published_by: 'alex@mobile1st.io',
      snippet: JSON.stringify(
        {
          script_type: 'ugc_testimonial',
          duration_target: 30,
          tone_override: 'conversational, mom-to-mom',
        },
        null,
        2,
      ),
    },
  },
  fringesport: {
    get_creative_brief: {
      version: '1.1.0',
      published_by: 'maya@mobile1st.io',
      snippet: JSON.stringify(
        {
          required_sections: ['persona_profile', 'performance_context', 'product_highlight'],
          tone: 'high-energy, authentic, community-driven',
        },
        null,
        2,
      ),
    },
    generate_creative_angles: {
      version: '1.0.0',
      published_by: 'maya@mobile1st.io',
      snippet: JSON.stringify({ preferred_archetypes: ['transformation', 'authority'], num_angles: 5 }, null, 2),
    },
  },
};

// ---------------------------------------------------------------------------
// Client detail panel
// ---------------------------------------------------------------------------

function ToolBindingRow({
  tool,
  covered,
  clientUid,
}: {
  tool: string;
  covered: boolean;
  clientUid: string;
}) {
  const [open, setOpen] = useState(false);
  const meta = TOOL_META[tool] ?? { label: tool, icon: Code, color: 'text-slate-500' };
  const binding = MOCK_BINDINGS[clientUid]?.[tool];

  return (
    <div
      className={clsx(
        'border rounded-xl overflow-hidden transition-all',
        covered ? 'border-slate-200 bg-white' : 'border-dashed border-slate-200 bg-slate-50/50',
      )}
    >
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
        onClick={() => covered && setOpen((o) => !o)}
        disabled={!covered}
      >
        <meta.icon size={15} className={covered ? meta.color : 'text-slate-300'} />
        <span className={clsx('flex-1 text-sm font-medium', covered ? 'text-navy-900' : 'text-slate-400')}>
          {meta.label}
        </span>

        {covered ? (
          <>
            <span className="text-[10px] font-mono text-emerald-600 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded">
              {binding?.version ?? 'bound'}
            </span>
            <CheckCircle2 size={14} className="text-emerald-500" />
            {open ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
          </>
        ) : (
          <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
            platform default
          </span>
        )}
      </button>

      {open && binding && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-2">
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <span>Published by <strong>{binding.published_by}</strong></span>
            <span className="font-mono">v{binding.version}</span>
          </div>
          <pre className="text-[11px] font-mono bg-slate-50 border border-slate-100 rounded-lg p-3 overflow-auto max-h-44 text-slate-700">
            {binding.snippet}
          </pre>
        </div>
      )}
    </div>
  );
}

function ClientDetail({ client, coreTools }: { client: ClientCoverage; coreTools: string[] }) {
  return (
    <div className="space-y-4">
      {/* Coverage summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-slate-100 rounded-xl px-4 py-3 text-center">
          <p className="text-2xl font-bold text-navy-900">{client.coverage_pct}%</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Tool Coverage</p>
        </div>
        <div className="bg-white border border-slate-100 rounded-xl px-4 py-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{client.covered_tools.length}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Bound tools</p>
        </div>
        <div className="bg-white border border-slate-100 rounded-xl px-4 py-3 text-center">
          <p className="text-2xl font-bold text-amber-500">{client.missing_tools.length}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Using defaults</p>
        </div>
      </div>

      {/* Tool bindings */}
      <div className="space-y-2">
        <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400">
          Tool bindings — what the SkillResolver applies on every call
        </p>
        {coreTools.map((tool) => (
          <ToolBindingRow
            key={tool}
            tool={tool}
            covered={client.covered_tools.includes(tool)}
            clientUid={client.client_uid}
          />
        ))}
      </div>

      {/* Missing tools callout */}
      {client.missing_tools.length > 0 && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
          <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-amber-700">
              {client.missing_tools.length} tool{client.missing_tools.length > 1 ? 's' : ''} using platform defaults
            </p>
            <p className="text-[11px] text-amber-600 mt-0.5">
              Publish a skill binding for{' '}
              <span className="font-mono">{client.missing_tools.join(', ')}</span> to enforce your
              org's creative process for {client.client_uid}.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function ClientPlaybooksPage() {
  const { clients, coreTools, loading, error, fetch: fetchCoverage } = useClientCoverageStore();
  const { fetchSkills } = useSkillRegistryStore();
  const [selectedClient, setSelectedClient] = useState<ClientCoverage | null>(null);

  useEffect(() => {
    fetchCoverage(DEFAULT_ORG);
    fetchSkills('published');
  }, [fetchCoverage, fetchSkills]);

  const fullCount = clients.filter((c) => c.status === 'full').length;
  const partialCount = clients.filter((c) => c.status === 'partial').length;
  const noneCount = clients.filter((c) => c.status === 'none').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
          <BookMarked size={22} className="text-brand-coral" />
          Client Playbooks
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Per-client creative intelligence — the skill bindings applied on every MCP tool call for each account.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard value={fullCount} label="Full coverage" color="text-emerald-600" icon={CheckCircle2} />
        <StatCard value={partialCount} label="Partial coverage" color="text-amber-600" icon={AlertTriangle} />
        <StatCard value={noneCount} label="No binding" color="text-red-500" icon={XCircle} />
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client list */}
        <div className="space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400 px-1">Accounts</p>
          {loading ? (
            <div className="flex items-center gap-2 py-8 text-slate-400 text-sm">
              <Loader2 size={14} className="animate-spin" />
              Loading…
            </div>
          ) : (
            clients.map((client) => (
              <button
                key={client.client_uid}
                onClick={() => setSelectedClient(client)}
                className={clsx(
                  'w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all',
                  selectedClient?.client_uid === client.client_uid
                    ? 'bg-white border-brand-coral/30 shadow-soft'
                    : 'bg-white border-slate-100 hover:border-slate-200',
                )}
              >
                <StatusIcon status={client.status} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-navy-900 truncate">{client.client_uid}</p>
                  <CoverageBar pct={client.coverage_pct} />
                </div>
                <ChevronRight
                  size={14}
                  className={clsx(
                    'flex-shrink-0 transition-colors',
                    selectedClient?.client_uid === client.client_uid ? 'text-brand-coral' : 'text-slate-300',
                  )}
                />
              </button>
            ))
          )}
        </div>

        {/* Detail panel */}
        <div className="lg:col-span-2">
          {selectedClient ? (
            <div className="bg-slate-50 rounded-2xl border border-slate-100 p-5 space-y-1">
              <div className="flex items-center gap-2 mb-4">
                <Shield size={16} className="text-brand-coral" />
                <h2 className="text-base font-bold text-navy-900">{selectedClient.client_uid}</h2>
                <span
                  className={clsx(
                    'text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase',
                    selectedClient.status === 'full'
                      ? 'bg-emerald-100 text-emerald-700'
                      : selectedClient.status === 'partial'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-red-50 text-red-500',
                  )}
                >
                  {selectedClient.status}
                </span>
              </div>
              <ClientDetail client={selectedClient} coreTools={coreTools} />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full min-h-64 text-slate-400 text-sm bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              Select a client to view its playbook
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({
  value,
  label,
  color,
  icon: Icon,
}: {
  value: number;
  label: string;
  color: string;
  icon: React.ElementType;
}) {
  return (
    <div className="bg-white border border-slate-100 rounded-xl px-4 py-4 flex items-center gap-3">
      <Icon size={20} className={color} />
      <div>
        <p className={clsx('text-2xl font-bold', color)}>{value}</p>
        <p className="text-[10px] text-slate-400 mt-0.5">{label}</p>
      </div>
    </div>
  );
}
